import { useCallback, useMemo } from 'react';
import type { CSSProperties } from 'react';
import { theme, type MenuProps } from 'antd';
import { AppButton, AppDropdown } from '@/components/atoms';
import type { MedicalAid, PatientFormData } from '@pages/main/patient/types';
import {
  coercePersistedHistoryRowId,
  getMedicalAidHistoryDropdownParts,
  MEDICAL_AID_HISTORY_START_DATE_COLUMN_CH,
  medicalAidHistoryHeaderEligibleLegacy,
  sortMedicalAidsForHistoryDropdown,
} from '@pages/main/patient/utils/patientHistoryHelpers';
import { historyDropdownPopupStyle } from './PatientSectionShared.helpers';

interface MedicalAidHistoryMenuProps {
  isEditMode: boolean;
  isCurrent: boolean;
  disabled: boolean;
  patientData?: PatientFormData | null;
  watchedMedicalAidId: unknown;
  medicalAidHistoryDraftByUrl: boolean;
  onHistorySwitch?: (
    section: 'patient_information' | 'health_insurance_card' | 'medical_aid',
    historyId: number
  ) => void;
  onHistoryDelete?: (
    section: 'patient_information' | 'health_insurance_card' | 'medical_aid',
    historyId: number
  ) => void;
  onMedicalAidHistoryCreateCancel?: () => void | Promise<void>;
}

export function useMedicalAidHistoryMenu({
  isEditMode,
  isCurrent,
  disabled,
  patientData,
  watchedMedicalAidId,
  medicalAidHistoryDraftByUrl,
  onHistorySwitch,
  onHistoryDelete,
  onMedicalAidHistoryCreateCancel,
}: MedicalAidHistoryMenuProps) {
  const { token } = theme.useToken();

  const historyCreateCancelButtonStyle = disabled
    ? undefined
    : {
        '--patient-history-cancel-bg': token.colorWarning,
        '--patient-history-cancel-bg-hover': token.colorWarningHover,
        '--patient-history-cancel-border': token.colorWarning,
        '--patient-history-cancel-border-hover': token.colorWarningHover,
        '--patient-history-cancel-text': token.colorTextLightSolid,
      } as CSSProperties;

  /** BD 3.3: desc by 適用開始日; label {start}から{end} */
  const sortedMedicalAids = useMemo((): MedicalAid[] => {
    const aids = patientData?.medical_aids;
    return aids?.length ? sortMedicalAidsForHistoryDropdown(aids) : [];
  }, [patientData?.medical_aids]);

  const currentMedicalAidRow =
    patientData?.medical_aids?.find(a => a.is_current) || patientData?.medical_aids?.[0];
  const medicalAidRowCount = patientData?.medical_aids?.length ?? 0;

  const idFromPatientData =
    coercePersistedHistoryRowId(currentMedicalAidRow?.id) ??
    coercePersistedHistoryRowId(
      (currentMedicalAidRow as { medical_aid_id?: unknown } | undefined)?.medical_aid_id
    );
  const watchedMedicalAidIdCoerced = coercePersistedHistoryRowId(watchedMedicalAidId);
  const displayedMedicalAidId = medicalAidHistoryDraftByUrl
    ? watchedMedicalAidIdCoerced
    : (watchedMedicalAidIdCoerced ?? idFromPatientData);
  const hasPersistedMedicalAidId = displayedMedicalAidId != null;

  const legacyMedicalAidHistoryHeaderEligible = useMemo(
    () => medicalAidHistoryHeaderEligibleLegacy(patientData?.medical_aids),
    [patientData?.medical_aids]
  );

  const showMedicalAidHistoryCreateCancel =
    isEditMode &&
    !!patientData?.id &&
    medicalAidRowCount > 0 &&
    (medicalAidHistoryDraftByUrl ||
      (legacyMedicalAidHistoryHeaderEligible && !hasPersistedMedicalAidId));

  const showMedicalAidHistoryHeader =
    isEditMode &&
    !!patientData?.id &&
    medicalAidRowCount > 0 &&
    (showMedicalAidHistoryCreateCancel || legacyMedicalAidHistoryHeaderEligible);

  const handleMedicalAidHistorySwitch = useCallback(
    (historyId: number, _source: 'new-history' | 'existing-row') => {
      onHistorySwitch?.('medical_aid', historyId);
    },
    [onHistorySwitch]
  );

  const historyRecordMenuItems = useMemo(
    () =>
      sortedMedicalAids.map(aid => {
        const parts = getMedicalAidHistoryDropdownParts(aid);
        const startColStyle = {
          width: `${MEDICAL_AID_HISTORY_START_DATE_COLUMN_CH}ch`,
        } as const;

        return {
          key: aid.id?.toString() || `aid-${aid.application_start_date}`,
          label: (
            <span className="inline-flex max-w-none items-start gap-2 whitespace-nowrap">
              <i
                className={
                  aid.is_current
                    ? 'fa fa-dot-circle-o mt-0.5 shrink-0 text-blue-600'
                    : 'fa fa-circle-o mt-0.5 shrink-0 text-gray-500'
                }
                aria-hidden="true"
              />
              <span className="inline-flex min-w-0 flex-nowrap items-baseline gap-x-0 text-sm">
                <span
                  className="inline-block shrink-0 whitespace-nowrap pr-1 text-left font-mono tabular-nums tracking-tight"
                  style={startColStyle}
                >
                  {parts.startYmd}
                </span>
                <span className="shrink-0 whitespace-nowrap">から</span>
                <span
                  className="inline-block shrink-0 whitespace-nowrap text-left font-mono tabular-nums tracking-tight"
                  style={startColStyle}
                >
                  {parts.endYmd}
                </span>
              </span>
            </span>
          ),
          onClick: () => handleMedicalAidHistorySwitch(aid.id!, 'existing-row'),
        };
      }),
    [sortedMedicalAids, handleMedicalAidHistorySwitch]
  );

  const historyDropdownItems = useMemo((): MenuProps['items'] => {
    const newHistoryLabel =
      isEditMode && !isCurrent ? '新しい履歴を入力する' : '新しい情報を入力する';
    const items: MenuProps['items'] = [
      {
        key: 'new-history',
        label: newHistoryLabel,
        className: 'text-blue-600',
        onClick: () => handleMedicalAidHistorySwitch(0, 'new-history'),
      },
    ];
    // A2 edit.blade: divider + list only when count() > 1; single row -> 新しい情報のみ.
    if (historyRecordMenuItems.length > 1) {
      items.push({ type: 'divider', key: 'divider-medical-aid-history' });
      items.push(...historyRecordMenuItems);
    }
    return items;
  }, [historyRecordMenuItems, isCurrent, isEditMode, handleMedicalAidHistorySwitch]);

  const historyMenu = showMedicalAidHistoryHeader ? (
    <div className="flex gap-2">
      {showMedicalAidHistoryCreateCancel && onMedicalAidHistoryCreateCancel ? (
        <AppButton
          type="default"
          size="small"
          className="!border-[var(--patient-history-cancel-border)] !bg-[var(--patient-history-cancel-bg)] !text-[var(--patient-history-cancel-text)] hover:!border-[var(--patient-history-cancel-border-hover)] hover:!bg-[var(--patient-history-cancel-bg-hover)]"
          style={historyCreateCancelButtonStyle}
          onClick={() => void onMedicalAidHistoryCreateCancel()}
          disabled={disabled}
        >
          履歴作成取消
        </AppButton>
      ) : (
        <>
          {/*
           * A2: 履歴削除 float-left, 履歴 float-right — DOM order: delete then dropdown.
           */}
          {onHistoryDelete &&
            !!patientData?.id &&
            medicalAidRowCount > 1 &&
            !medicalAidHistoryDraftByUrl &&
            hasPersistedMedicalAidId && (
              <AppButton
                type="default"
                size="small"
                danger
                onClick={() => onHistoryDelete('medical_aid', displayedMedicalAidId as number)}
                disabled={disabled}
                className={disabled ? '!bg-white/20 !text-white/40 !border-white/10' : ''}
              >
                履歴削除
              </AppButton>
            )}
          <AppDropdown
            menu={{
              items: historyDropdownItems,
              style: historyDropdownPopupStyle,
            }}
            styles={{
              root: historyDropdownPopupStyle,
            }}
            trigger={['click']}
            placement="bottomRight"
            disabled={disabled}
          >
            <AppButton
              type="default"
              size="small"
              disabled={disabled}
              className={disabled ? '!bg-white/20 !text-white/60 !border-white/20' : ''}
            >
              履歴 <i className="fa fa-angle-down" aria-hidden="true" />
            </AppButton>
          </AppDropdown>
        </>
      )}
    </div>
  ) : null;

  return {
    historyMenu,
    showMedicalAidHistoryCreateCancel,
  };
}
